import { type DatePickerCalendarContextValue, DatePickerCalendarProvider } from '../Calendar/CalendarContext';
import { type DatePickerDropdownContextValue, DatePickerDropdownProvider } from '../Dropdown/DropdownContext';
import { type DatePickerValidationContextValue, DatePickerValidationProvider } from '../Input/ValidationContext';
import { type DatePickerActionsContextValue, DatePickerActionsProvider } from './ActionsContext';
import { type DatePickerConfigContextValue, DatePickerConfigProvider } from './ConfigContext';

/**
 * Props for SgDatePickerProvider
 * This interface defines all the props needed by the provider
 */
export interface SgDatePickerProviderProps {
  children: React.ReactNode;

  // Configuration
  config: DatePickerConfigContextValue;

  // Calendar
  calendar: DatePickerCalendarContextValue;

  // Dropdown
  dropdown: DatePickerDropdownContextValue;

  // Validation
  validation: DatePickerValidationContextValue;

  // Actions
  actions: DatePickerActionsContextValue;
}

/**
 * Main provider component that wraps all context providers
 * This component ensures optimal performance by:
 * - Separating contexts by update frequency
 * - Memoizing context values
 * - Providing stable references for callbacks
 *
 * @example
 * ```tsx
 * <SgDatePickerProvider
 *   config={configValue}
 *   calendar={calendarValue}
 *   dropdown={dropdownValue}
 *   presets={presetsValue}
 *   validation={validationValue}
 *   actions={actionsValue}
 *   uiState={uiStateValue}
 * >
 *   <DatePickerContent />
 * </SgDatePickerProvider>
 * ```
 */
export function SgDatePickerProvider(props: SgDatePickerProviderProps) {
  const { children, config, calendar, dropdown, validation, actions } = props;

  return (
    <DatePickerConfigProvider value={config}>
      <DatePickerCalendarProvider value={calendar}>
        <DatePickerDropdownProvider value={dropdown}>
          <DatePickerValidationProvider value={validation}>
            <DatePickerActionsProvider value={actions}>{children}</DatePickerActionsProvider>
          </DatePickerValidationProvider>
        </DatePickerDropdownProvider>
      </DatePickerCalendarProvider>
    </DatePickerConfigProvider>
  );
}
